import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  filterFns,
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useAdminApiQuery } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';
import { PageContainer } from '@/components/page-container';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { DataTable } from '@/components/super-admin/data-table';
import { StatCards, type Stats } from '@/components/super-admin/stat-cards';
import { unitLabel } from '@/lib/usage-unit';

/** Row of GET /super-admin/organizations (flat-model operator view). */
interface OrganizationRow {
  id: string;
  name: string;
  planSlug: string;
  trialDaysLeft: number | null;
  hasCard: boolean;
  scheduledPlanSlug: string | null;
  overflowMode: 'none' | 'auto_upgrade' | 'auto_top_up';
  workspaceCount: number;
  memberCount: number;
  channelCount: number;
  memberEmails: string[];
  monthUsage: number;
  monthLimit: number;
  /** Live top-up reserve in usageUnit (AIT-626); absorbs overage past monthLimit. */
  reserveBalance: number;
  /** The meter monthUsage/monthLimit are counted in (AIT-436). */
  usageUnit: 'messages' | 'actions';
  monthUsagePercentage: number;
  /** Instant the org's quota-period usage counter restarts (per-org anchor; calendar month when unanchored). */
  usageResetAt: string;
  createdAt: string;
}

/** First 2 member emails; overflow collapses to "…" with the full list on hover. */
function MemberEmailsCell({ emails }: { emails: string[] }) {
  if (emails.length === 0) return <span className="text-muted-foreground">—</span>;
  const shown = emails.slice(0, 2).join(', ');
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="cursor-default" />}>
        {emails.length > 2 ? `${shown}, …` : shown}
      </TooltipTrigger>
      <TooltipContent className="max-w-72">
        <div className="space-y-0.5">
          {emails.map((e) => (
            <div key={e}>{e}</div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function BillingIntentCell({ row }: { row: OrganizationRow }) {
  return (
    <div className="flex max-w-48 flex-wrap gap-1">
      <Badge variant={row.hasCard ? 'success' : 'outline'}>
        {row.hasCard ? 'Card on file' : 'No card'}
      </Badge>
      {row.scheduledPlanSlug && (
        <Badge variant="warning">
          <span className="capitalize">{row.scheduledPlanSlug}</span>
          {row.trialDaysLeft !== null ? ' after trial' : ' scheduled'}
        </Badge>
      )}
      {(row.overflowMode === 'auto_upgrade' || row.overflowMode === 'auto_top_up') && (
        <Badge variant="secondary">
          {row.overflowMode === 'auto_upgrade' ? 'Auto upgrade' : 'Auto top-up'}
        </Badge>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
}

/** UTC-fixed: the usage reset is a server-side (UTC) calendar-month edge, so a
 *  westward operator must not see it rendered as the previous day. */
function formatUtcDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

export function SuperAdminOrganizationsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data: stats = null, isLoading: statsLoading } = useAdminApiQuery<Stats>(
    qk.superAdmin('channels', 'stats'),
    '/super-admin/channels/stats',
  );
  const { data, isLoading } = useAdminApiQuery<OrganizationRow[]>(
    qk.superAdmin('organizations'),
    '/super-admin/organizations',
  );
  // Memoized: an unmemoized array remounts every cell on each render.
  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<ColumnDef<OrganizationRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: true,
        meta: { cellClassName: 'font-medium', sortLabel: 'name' },
      },
      {
        accessorKey: 'planSlug',
        header: 'Plan',
        enableSorting: true,
        meta: { headerClassName: 'w-[120px]', cellClassName: 'capitalize', sortLabel: 'plan' },
      },
      {
        accessorKey: 'trialDaysLeft',
        header: 'Trial left',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[100px]', cellClassName: 'tabular-nums', sortLabel: 'trial days left' },
        cell: ({ row }) => {
          const days = row.original.trialDaysLeft;
          return days == null ? '—' : `${days} ${days === 1 ? 'day' : 'days'}`;
        },
      },
      {
        id: 'billingIntent',
        accessorFn: (row) =>
          [
            row.hasCard ? 'Card on file' : 'No card',
            row.scheduledPlanSlug
              ? `${row.scheduledPlanSlug} ${row.trialDaysLeft !== null ? 'after trial' : 'scheduled'}`
              : '',
            row.overflowMode === 'auto_upgrade'
              ? 'Auto upgrade'
              : row.overflowMode === 'auto_top_up'
                ? 'Auto top-up'
                : '',
          ]
            .filter(Boolean)
            .join(' '),
        header: 'Billing intent',
        meta: { headerClassName: 'w-[200px]' },
        cell: ({ row }) => <BillingIntentCell row={row.original} />,
      },
      {
        accessorKey: 'workspaceCount',
        header: 'Workspaces',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[110px]', sortLabel: 'workspaces' },
      },
      {
        accessorKey: 'memberCount',
        header: 'Members',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[100px]', sortLabel: 'members' },
      },
      {
        accessorKey: 'channelCount',
        header: 'Channels',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[100px]', sortLabel: 'channels' },
      },
      {
        id: 'users',
        accessorFn: (row) => row.memberEmails.join(' '),
        header: 'Users',
        cell: ({ row }) => <MemberEmailsCell emails={row.original.memberEmails} />,
      },
      {
        accessorKey: 'monthUsage',
        header: 'Pool Usage (period)',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[220px]', sortLabel: 'quota-period usage' },
        cell: ({ row }) => (
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <span className="text-sm tabular-nums">
                {row.original.monthUsage.toLocaleString()} /{' '}
                {row.original.monthLimit.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">
                {unitLabel(row.original.usageUnit)}
              </span>
              <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full ${row.original.monthUsagePercentage >= 100 && row.original.reserveBalance <= 0 ? 'bg-destructive' : 'bg-primary'}`}
                  style={{ width: `${Math.min(100, row.original.monthUsagePercentage)}%` }}
                />
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Resets {formatUtcDate(row.original.usageResetAt)}
              {row.original.reserveBalance > 0 &&
                ` · ${row.original.reserveBalance.toLocaleString()} reserve left`}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[130px]', sortLabel: 'created' },
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => row.id,
    defaultColumn: { enableSorting: false },
    enableMultiSort: false,
    sortDescFirst: false,
    state: { globalFilter: search },
    // AIT-637: public id (org_/ws_) is not a column, so fold it into the search.
    globalFilterFn: (row, columnId, value, addMeta) =>
      row.original.id.toLowerCase().includes(String(value).toLowerCase()) ||
      filterFns.includesString(row, columnId, value, addMeta),
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <PageContainer variant="fluid" className="space-y-6">
      <StatCards stats={stats} isLoading={statsLoading} />
      <Input
        placeholder="Search organizations by name, plan, or id..."
        aria-label="Search organizations"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />
      <div className="rounded-md border">
        <DataTable
          table={table}
          isLoading={isLoading}
          emptyState="No organizations found."
          onRowActivate={(row) => navigate(`/super-admin/organizations/${row.original.id}`)}
        />
      </div>
    </PageContainer>
  );
}
